import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Stack,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import {
    Print as IconPrint,
    PictureAsPdf as IconPdf,
    TableView as IconExcel,
    Description as IconWord,
    ArrowBack as IconVolver,
    Assessment as IconReport
} from '@mui/icons-material';
import moment from 'moment';
import { Link, useSearchParams } from 'react-router-dom';

import { getsCajaChica, getsMovimientos } from '../../services/cajaChicaService';
import FiltroFilial from '../../components/common/FiltroFilial';
import { useFiltroFilial } from '../../hooks/useFiltroFilial';
import { exportarExcel, exportarPdf, exportarWord, imprimirReporte } from '../../services/reporteExportService';
import { registrarEjecucionReporte } from '../../services/bitacoraReporteService';

const ReporteCajaChica = () => {
    const [searchParams] = useSearchParams();
    const idCajaParam = parseInt(searchParams.get('idCajaChica') || '0', 10);

    const { filiales, idFilial, setIdFilial, bloqueado } = useFiltroFilial();

    const [cajas, setCajas] = useState([]);
    const [idCajaChica, setIdCajaChica] = useState(idCajaParam);
    const [fechaInicio, setFechaInicio] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [fechaFin, setFechaFin] = useState(moment().endOf('month').format('YYYY-MM-DD'));

    const [movimientos, setMovimientos] = useState([]);
    const [cargando, setCargando] = useState(false);

    useEffect(() => {
        getsCajaChica({ idFilial: idFilial || 0 })
            .then((data) => {
                setCajas(data || []);
                if (data && data.length > 0 && !idCajaChica) {
                    setIdCajaChica(data[0].id);
                }
            })
            .catch((err) => console.error(err));
    }, [idFilial, idCajaChica]);

    const generarReporte = useCallback(async () => {
        if (!idCajaChica) {
            setMovimientos([]);
            return;
        }

        setCargando(true);
        const t0 = performance.now();
        try {
            const data = await getsMovimientos({
                idCajaChica,
                fechaInicio: fechaInicio ? moment(fechaInicio).toISOString() : null,
                fechaFin: fechaFin ? moment(fechaFin).toISOString() : null,
                estado: 'APROBADO'
            });
            setMovimientos(data || []);

            registrarEjecucionReporte({
                reportPath: '/cajachica/reportes',
                format: 'HTML',
                reportAction: 'Render',
                timeRendering: Math.round(performance.now() - t0),
                rowCount: data?.length || 0,
                status: 'rsSuccess'
            });
        } catch (err) {
            console.error('Error generando reporte caja chica:', err);
        } finally {
            setCargando(false);
        }
    }, [idCajaChica, fechaInicio, fechaFin]);

    useEffect(() => {
        generarReporte();
    }, [generarReporte]);

    const cajaSeleccionada = cajas.find((c) => c.id === idCajaChica);

    // Totales y agrupación por concepto de gasto
    const totalEgresos = movimientos.reduce((acc, m) => acc + (m.montoEgreso || 0), 0);
    const totalIngresos = movimientos.reduce((acc, m) => acc + (m.montoIngreso || 0), 0);
    const saldoFinal = cajaSeleccionada?.saldoDisponible || 0;

    const desgloseConceptos = Object.values(
        movimientos.reduce((acc, m) => {
            if (m.montoEgreso > 0) {
                const key = m.conceptoGastoCatalogo || m.conceptoGasto || 'Otros';
                if (!acc[key]) acc[key] = { concepto: key, total: 0, count: 0 };
                acc[key].total += m.montoEgreso;
                acc[key].count += 1;
            }
            return acc;
        }, {})
    ).sort((a, b) => b.total - a.total);

    const columnasExport = [
        { header: 'N° Op.', key: 'op' },
        { header: 'Fecha', key: 'fecha' },
        { header: 'Tipo Mov.', key: 'tipo' },
        { header: 'Concepto / Glosa', key: 'concepto' },
        { header: 'Comprobante', key: 'comprobante' },
        { header: 'Proveedor / Rendidor', key: 'proveedor' },
        { header: 'Egreso (S/)', key: 'egreso' },
        { header: 'Ingreso (S/)', key: 'ingreso' },
        { header: 'Saldo (S/)', key: 'saldo' }
    ];

    const filasExport = movimientos.map((m) => ({
        op: m.numeroOperacion,
        fecha: moment(m.fechaMovimiento).format('DD/MM/YYYY HH:mm'),
        tipo: m.tipoMovimiento,
        concepto: m.conceptoGasto,
        comprobante: m.serieNumeroComprobante ? `${m.tipoComprobante || ''} ${m.serieNumeroComprobante}` : '-',
        proveedor: m.razonSocialProveedor || m.nombreRendidor || '-',
        egreso: Number(m.montoEgreso).toFixed(2),
        ingreso: Number(m.montoIngreso).toFixed(2),
        saldo: Number(m.saldoResultante).toFixed(2)
    }));

    const tituloReporte = `Arqueo y Liquidación de Caja Chica - ${cajaSeleccionada?.codigo || ''} (${cajaSeleccionada?.filial || ''})`;

    const handleImprimir = () => {
        imprimirReporte({
            titulo: tituloReporte,
            subtitulo: `Responsable: ${cajaSeleccionada?.responsable || ''} | Periodo: ${moment(fechaInicio).format('DD/MM/YYYY')} al ${moment(fechaFin).format('DD/MM/YYYY')}`,
            columnas: columnasExport,
            filas: filasExport
        });
    };

    const handlePdf = () => {
        exportarPdf({
            nombreArchivo: `Arqueo_Caja_${cajaSeleccionada?.codigo || 'CCH'}_${moment().format('YYYYMMDD')}.pdf`,
            titulo: tituloReporte,
            subtitulo: `Responsable: ${cajaSeleccionada?.responsable || ''} | Saldo Disponible: S/ ${Number(saldoFinal).toFixed(2)}`,
            columnas: columnasExport,
            filas: filasExport
        });
    };

    const handleExcel = () => {
        exportarExcel({
            nombreArchivo: `Arqueo_Caja_${cajaSeleccionada?.codigo || 'CCH'}_${moment().format('YYYYMMDD')}.xlsx`,
            hojas: [{ nombre: 'Movimientos', columnas: columnasExport, filas: filasExport }]
        });
    };

    const handleWord = () => {
        exportarWord({
            nombreArchivo: `Arqueo_Caja_${cajaSeleccionada?.codigo || 'CCH'}_${moment().format('YYYYMMDD')}.docx`,
            titulo: tituloReporte,
            columnas: columnasExport,
            filas: filasExport
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Cabecera */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Button
                        component={Link}
                        to="/cajachica"
                        startIcon={<IconVolver />}
                        size="small"
                        sx={{ textTransform: 'none' }}
                    >
                        Volver a Gestión de Caja Chica
                    </Button>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1f2937', mt: 1 }}>
                        <IconReport sx={{ verticalAlign: 'middle', mr: 1, color: '#0284c7' }} />
                        {tituloReporte}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Arqueo oficial de fondos fijos, liquidación periódica y clasificación de comprobantes de gasto.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<IconPrint />} onClick={handleImprimir} size="small">
                        Imprimir
                    </Button>
                    <Button variant="outlined" color="error" startIcon={<IconPdf />} onClick={handlePdf} size="small">
                        PDF
                    </Button>
                    <Button variant="outlined" color="success" startIcon={<IconExcel />} onClick={handleExcel} size="small">
                        Excel
                    </Button>
                    <Button variant="outlined" color="primary" startIcon={<IconWord />} onClick={handleWord} size="small">
                        Word
                    </Button>
                </Stack>
            </Stack>

            {/* Filtros */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#ffffff' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <FiltroFilial
                            filiales={filiales}
                            idFilial={idFilial}
                            setIdFilial={setIdFilial}
                            bloqueado={bloqueado}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Caja Chica</InputLabel>
                            <Select
                                value={idCajaChica}
                                onChange={(e) => setIdCajaChica(e.target.value)}
                                label="Caja Chica"
                            >
                                {cajas.map((c) => (
                                    <MenuItem key={c.id} value={c.id}>
                                        {c.codigo} - {c.nombre} ({c.estado})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Desde"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Hasta"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={fechaFin}
                            onChange={(e) => setFechaFin(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Resumen del Arqueo */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ bgcolor: '#f8fafc' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                FONDO FIJO ASIGNADO
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#334155' }}>
                                S/ {Number(cajaSeleccionada?.montoFondoFijo || 0).toFixed(2)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ bgcolor: '#fef2f2' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                TOTAL GASTOS RENDIDOS
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#b91c1c' }}>
                                S/ {totalEgresos.toFixed(2)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ bgcolor: '#f0fdf4' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                REPOSICIONES RECIBIDAS
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#15803d' }}>
                                S/ {totalIngresos.toFixed(2)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ bgcolor: '#eff6ff' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                SALDO ACTUAL DISPONIBLE
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1d4ed8' }}>
                                S/ {Number(saldoFinal).toFixed(2)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Desglose por Conceptos de Gasto */}
            {desgloseConceptos.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#334155' }}>
                        Desglose de Gastos por Categoría / Concepto
                    </Typography>
                    <Grid container spacing={1.5}>
                        {desgloseConceptos.map((item, idx) => {
                            const porcentaje = totalEgresos > 0 ? (item.total / totalEgresos) * 100 : 0;
                            return (
                                <Grid item xs={12} sm={4} md={3} key={idx}>
                                    <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1, bgcolor: '#fafafa' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {item.concepto}
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#dc2626', my: 0.25 }}>
                                            S/ {item.total.toFixed(2)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {item.count} comprobante(s) ({porcentaje.toFixed(1)}%)
                                        </Typography>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>
            )}

            {/* Tabla Detallada de Rendiciones */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Relación Detallada de Comprobantes y Rendiciones
                    </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 550 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>N°</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Concepto</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Comprobante</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Proveedor / Rendidor</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Egreso (S/)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Ingreso (S/)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Saldo Resultante (S/)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cargando ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : movimientos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        No hay movimientos registrados para este periodo.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                movimientos.map((m) => (
                                    <TableRow key={m.id} hover>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>#{m.numeroOperacion}</TableCell>
                                        <TableCell>{moment(m.fechaMovimiento).format('DD/MM/YYYY HH:mm')}</TableCell>
                                        <TableCell>{m.tipoMovimiento}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.conceptoGasto}</Typography>
                                            {m.conceptoGastoCatalogo && m.conceptoGastoCatalogo !== m.conceptoGasto && (
                                                <Typography variant="caption" color="text.secondary">{m.conceptoGastoCatalogo}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {m.serieNumeroComprobante ? `${m.tipoComprobante || ''} ${m.serieNumeroComprobante}` : '-'}
                                        </TableCell>
                                        <TableCell>{m.razonSocialProveedor || m.nombreRendidor || '-'}</TableCell>
                                        <TableCell align="right" sx={{ color: '#dc2626', fontWeight: m.montoEgreso > 0 ? 'bold' : 'normal' }}>
                                            {m.montoEgreso > 0 ? Number(m.montoEgreso).toFixed(2) : '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#16a34a', fontWeight: m.montoIngreso > 0 ? 'bold' : 'normal' }}>
                                            {m.montoIngreso > 0 ? Number(m.montoIngreso).toFixed(2) : '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                            S/ {Number(m.saldoResultante).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default ReporteCajaChica;
